/* Dispatch 13 -- Adaptive viewport hooks.
 *
 * `useIsMobile()`  -> true when the window is narrower than 768px (Tailwind's
 *                    `md` breakpoint boundary).
 * `useIsStandalone()` -> true when the app is running as an installed PWA
 *                    (iOS Safari's `navigator.standalone` OR the
 *                    `display-mode: standalone` media query, which covers
 *                    Android Chrome + Windows Edge + desktop Chrome).
 *
 * Both hooks are SSR-safe (they short-circuit to `false` when `window` is
 * undefined) and use a single `matchMedia` listener each so mounting them
 * across many components is cheap.
 */
import { useEffect, useState } from "react";

const MOBILE_BREAKPOINT = 768; // px; matches Tailwind `md:` boundary

function readIsMobile() {
  if (typeof window === "undefined") return false;
  return window.innerWidth < MOBILE_BREAKPOINT;
}

/**
 * Live-updating boolean: is the viewport narrower than 768px?
 * Optional `override` parameter forces a value for testing / dev
 * (localStorage-driven previews). When `override` is a boolean it is
 * returned as-is without registering listeners.
 */
export function useIsMobile(override) {
  const [isMobile, setIsMobile] = useState(
    typeof override === "boolean" ? override : readIsMobile
  );

  useEffect(() => {
    if (typeof override === "boolean") {
      setIsMobile(override);
      return undefined;
    }
    if (typeof window === "undefined") return undefined;
    // matchMedia is more efficient than a resize listener and fires only
    // when the breakpoint boundary is actually crossed.
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    // Sync the initial value once (SSR guard above may have shipped a wrong
    // starting value into the render).
    setIsMobile(mq.matches);
    // addEventListener('change', ...) is the modern API; older Safari still
    // needs addListener. We attach whichever exists.
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, [override]);

  return isMobile;
}

function readIsStandalone() {
  if (typeof window === "undefined") return false;
  // iOS Safari legacy flag first (only Safari sets it).
  if (window.navigator && window.navigator.standalone === true) return true;
  // Modern browsers (Android Chrome, desktop Chrome, Edge, Firefox PWA).
  if (window.matchMedia) {
    try {
      return window.matchMedia("(display-mode: standalone)").matches;
    } catch (_) {
      return false;
    }
  }
  return false;
}

/**
 * True when the app is running as an installed PWA (not a browser tab).
 * Live-updates when the standalone media query changes (rare, but possible
 * when Chrome installs the app while it's already open).
 */
export function useIsStandalone() {
  const [standalone, setStandalone] = useState(readIsStandalone);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(display-mode: standalone)");
    const handler = () => setStandalone(readIsStandalone());
    handler();
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);

  return standalone;
}

// Non-hook accessors for imperative code (analytics, one-shot decisions).
export const isMobileViewport = readIsMobile;
export const isStandaloneApp = readIsStandalone;
